import type { V2BillingUpgradeDowngradeModalProps } from "../../../editor/components/AppModalTypes";

import * as React from "react";

import { useCurrentWorkspace } from "@editor/contexts/WorkspaceDashboardContext";
import { useSubscriptionDetails } from "@editor/hooks/subscription";
import useInitializeSubscriptionUpdateMutation from "@editor/hooks/trpc/useInitializeSubscriptionUpdateMutation";
import { useModal } from "@editor/hooks/useModal";
import Arrow from "@svg/billing/Arrow";

import { getPlanRank } from "@/features/workspace/shared/sharedBillingUtils";
import { getCountOfV2AdditionalShopsInSubscription } from "@/features/workspace/v2/V2BillingUtils";
import { V2PlanCardShell } from "@/features/workspace/v2/V2PlanCard";
import InlineAlert from "@replo/design-system/components/alert/InlineAlert";
import Button from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import {
  getV2BillingPlanInfoByTier,
  v1AdditionalPublishedElementsBundleProductUnitName,
  v1AdditionalShopProductUnitName,
} from "schemas/billing";
import { getCountOfProductUnitsInSubscription } from "schemas/utils/subscriptions";

import V2BillingSummary from "./V2BillingSummary";

export const V2BillingUpgradeDowngradeModal: React.FC<
  V2BillingUpgradeDowngradeModalProps
> = ({ newTier, newBillingInterval, newAdditionalShops }) => {
  const modal = useModal();
  const workspace = useCurrentWorkspace();

  const {
    initializeSubscriptionUpdate,
    isLoading: isLoadingSubscriptionUpdate,
  } = useInitializeSubscriptionUpdateMutation();

  const { data: activeSubscription } = useSubscriptionDetails();

  if (!workspace) {
    return null;
  }

  const currentTier = activeSubscription?.v2BillingTier ?? "free";

  const currentTierRank = getPlanRank(currentTier);
  const newTierRank = getPlanRank(newTier);

  let modalType: "upgrade" | "downgrade" | "update-configuration" =
    "update-configuration";

  if (currentTierRank < newTierRank) {
    modalType = "upgrade";
  } else if (currentTierRank > newTierRank) {
    modalType = "downgrade";
  }

  const currentPlanInfo = getV2BillingPlanInfoByTier(currentTier);
  const newPlanInfo = getV2BillingPlanInfoByTier(newTier);

  const headerCopy = exhaustiveSwitch({ type: modalType })({
    upgrade: {
      header: `Level Up to ${newPlanInfo.displayName}`,
      subheader:
        "Access premium features designed to help you work faster, smarter, and more effectively.",
    },
    downgrade: {
      header: `You're Downgrading to ${newPlanInfo.displayName}`,
      subheader: "Here's what will change:",
    },
    "update-configuration": {
      header: `You're Updating Your ${currentPlanInfo.displayName} Plan's Configuration`,
      subheader: "Here's what your updated plan will look like:",
    },
  });

  const isCurrentPlanYearly = activeSubscription?.billingInterval === "yearly";
  const currentPlanAdditionalShops = activeSubscription
    ? getCountOfV2AdditionalShopsInSubscription(activeSubscription)
    : 0;

  const isNewPlanYearly = newBillingInterval === "yearly";

  const closeModal = () => {
    modal.closeModal({ type: "v2BillingUpgradeDowngradeModal" });
  };

  const handleSubmitPlanChanges = async () => {
    // NOTE (Cole 2025-09-26): We must make sure that if there's an existing subscription,
    // it's loaded before we submit an update. I'm assuming that is the case here but if you
    // move this logic, be aware of this.

    // NOTE (Cole 2025-10-01): In the case when we are updating a v2 subscription while there is a v1 subscription
    // that has been cancelled but not ended yet, we will immediately end the v1 subscription.
    // The user should be entitled to finish their period on that v1 subscription but they won't be able to.
    // This is just the simplest solution to this edge case right now - if the user complains we will fix it for them.
    const currentV1AdditionalShops =
      activeSubscription && !activeSubscription.endedAt
        ? getCountOfProductUnitsInSubscription({
            subscription: activeSubscription,
            productUnitName: v1AdditionalShopProductUnitName,
          })
        : 0;

    const currentAdditionalPublishedElementBundles =
      activeSubscription && !activeSubscription.endedAt
        ? getCountOfProductUnitsInSubscription({
            subscription: activeSubscription,
            productUnitName: v1AdditionalPublishedElementsBundleProductUnitName,
          })
        : 0;

    const v1BillingTier =
      activeSubscription && !activeSubscription.endedAt
        ? activeSubscription.v1BillingTier
        : "free";

    await initializeSubscriptionUpdate({
      workspaceId: workspace.id,
      v1BillingTier: v1BillingTier,
      v2BillingTier: newTier,
      billingInterval: newBillingInterval,
      v1AdditionalShops: currentV1AdditionalShops,
      v2AdditionalShops: newAdditionalShops,
      additionalPublishedElementBundles:
        currentAdditionalPublishedElementBundles,
      source: "direct.billingDashboard",
    });
  };

  const handleConfirm = async () => {
    await handleSubmitPlanChanges();
    closeModal();
  };

  const handleCancel = () => {
    closeModal();
  };

  const footer = (
    <div className="flex flex-row justify-end gap-3">
      <Button variant="secondary" size="base" onClick={handleCancel}>
        {modalType === "update-configuration"
          ? "Keep your current configuration"
          : `Stay with ${currentPlanInfo.displayName}`}
      </Button>
      <Button
        variant="primary"
        size="base"
        onClick={handleConfirm}
        isLoading={isLoadingSubscriptionUpdate}
      >
        {exhaustiveSwitch({ type: modalType })({
          upgrade: "Confirm Upgrade",
          downgrade: "Confirm Downgrade",
          "update-configuration": "Confirm Changes",
        })}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={true}
      title={headerCopy.header}
      onOpenChange={(open) => {
        if (!open) {
          closeModal();
        }
      }}
      size="custom"
      UNSAFE_className="max-w-4xl"
      data-testid="v2-billing-upgrade-downgrade-modal"
      footer={footer}
    >
      <div className="flex flex-col gap-6 -mt-4">
        <div className="text-muted">{headerCopy.subheader}</div>

        <div className="flex gap-6 items-start">
          {/* Only show one plan card if they're changing configuration */}
          {modalType !== "update-configuration" && (
            <>
              <div className="flex-1">
                <V2PlanCardShell
                  plan={currentPlanInfo}
                  isYearly={isCurrentPlanYearly}
                  isCustomPlan={false}
                  isViewOnly={true}
                  v2AdditionalShops={currentPlanAdditionalShops}
                  handleShopsIncrement={() => {}}
                  handleShopsDecrement={() => {}}
                />
              </div>
              <div className="flex-shrink-0 self-center">
                <Arrow />
              </div>
            </>
          )}
          <div className="flex-1">
            <V2PlanCardShell
              plan={newPlanInfo}
              isYearly={isNewPlanYearly}
              isCustomPlan={false}
              isViewOnly={true}
              v2AdditionalShops={newAdditionalShops}
              handleShopsIncrement={() => {}}
              handleShopsDecrement={() => {}}
            />
          </div>
        </div>

        <V2BillingSummary
          activeSubscription={activeSubscription ?? undefined}
          isNewPlanYearly={isNewPlanYearly}
          newV2Tier={newTier}
          newV2AdditionalShops={newAdditionalShops}
        />
        {isNewPlanYearly &&
          activeSubscription?.billingInterval === "monthly" && (
            <InlineAlert variant="warning">
              You're switching to an annual plan across this entire workspace.
              Please note this plan is not refundable.
            </InlineAlert>
          )}
      </div>
    </Modal>
  );
};
